<template>
  <div class="prod-page py-3">
    <header class="prod-toolbar">
      <div class="prod-toolbar__title">
        <span class="prod-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Products</h1>
          <p>
            <span class="prod-toolbar__org">{{ activeOrganisationName || 'the current organisation' }}</span>
            · Manage the catalogue of products available for orders, packing and stock
          </p>
        </div>
      </div>
      <div class="prod-toolbar__actions">
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadData"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          color="primary"
          size="sm"
          class="create-btn"
          @click="openCreate"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>New Product</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="prod-alert">{{ errorMessage }}</CAlert>

    <BaseTable
      :items="filteredItems"
      :columns="columns"
      title="Catalogue"
      accent="linear-gradient(180deg, #0e5b46, #16a07a)"
      :loading="isLoading"
      :default-sort="{ column: 'name', direction: 'asc' }"
      striped
      hover
      bordered
      sticky-header
      class="prod-table"
    >
      <template #toolbar>
        <div class="prod-search-wrap">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="prod-search-icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="prod-input"
            placeholder="Search by name, SKU, barcode or shortcode…"
          />
          <button
            v-if="searchTerm"
            type="button"
            class="prod-search-clear"
            aria-label="Clear search"
            @click="searchTerm = ''"
          >×</button>
        </div>
      </template>

      <template #cell-image="{ row }">
        <div class="prod-thumb-wrap">
          <img v-if="row.image" :src="row.image" :alt="row.name" class="prod-thumb" loading="lazy" />
          <span v-else class="prod-thumb prod-thumb--placeholder" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
          </span>
        </div>
      </template>

      <template #cell-sku="{ row }">
        <span class="prod-sku">{{ row.sku }}</span>
      </template>

      <template #cell-name="{ row }">
        <div class="prod-name-cell">
          <span class="prod-name">{{ row.name }}</span>
          <span v-if="row.description" class="prod-description" :title="row.description">{{ row.description }}</span>
        </div>
      </template>

      <template #cell-shortcode="{ row }">
        <span v-if="row.shortcode" class="prod-shortcode-pill">{{ row.shortcode }}</span>
        <span v-else class="prod-muted">—</span>
      </template>

      <template #cell-barcode="{ row }">
        <span v-if="row.barcode" class="prod-barcode">{{ row.barcode }}</span>
        <span v-else class="prod-muted">—</span>
      </template>

      <template #cell-buying_price="{ row }">
        <span class="prod-num">{{ formatMoney(row.buying_price) }}</span>
      </template>

      <template #cell-selling_price="{ row }">
        <span class="prod-num prod-num--strong">{{ formatMoney(row.selling_price) }}</span>
      </template>

      <template #cell-weight="{ row }">
        <span class="prod-num">{{ formatWeight(row.weight) }}</span>
      </template>

      <template #cell-shipping_weight="{ row }">
        <span class="prod-num">{{ formatWeight(row.shipping_weight) }}</span>
      </template>

      <template #cell-actions="{ row }">
        <div class="prod-row-actions">
          <button type="button" class="prod-action-btn prod-action-btn--edit" @click="openEdit(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            <span>Edit</span>
          </button>
          <button type="button" class="prod-action-btn prod-action-btn--delete" @click="openDelete(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/>
              <path d="M14 11v6"/>
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </template>

      <template #empty>
        <div v-if="!items.length" class="prod-empty">
          <div class="prod-empty__title">No products yet</div>
          <div class="prod-empty__hint">
            <button type="button" class="prod-empty__link" @click="openCreate">Add the first product</button>
            for {{ activeOrganisationName || 'this organisation' }}.
          </div>
        </div>
        <div v-else class="prod-empty">
          <div class="prod-empty__title">No matches for "{{ searchTerm }}"</div>
          <div class="prod-empty__hint">
            Try a different keyword, or
            <button type="button" class="prod-empty__link" @click="searchTerm = ''">clear search</button>.
          </div>
        </div>
      </template>
    </BaseTable>

    <transition name="prod-fade">
      <div
        v-if="formOpen"
        class="prod-modal-backdrop"
        @click.self="closeForm"
      >
        <div class="prod-modal" role="dialog" aria-modal="true" :aria-label="formTitle">
          <header class="prod-modal__header">
            <div class="prod-modal__title-block">
              <span class="prod-modal__accent" aria-hidden="true"></span>
              <div>
                <h2 class="prod-modal__title">{{ formTitle }}</h2>
                <p class="prod-modal__subtitle">{{ formSubtitle }}</p>
              </div>
            </div>
            <button
              type="button"
              class="prod-modal__close"
              aria-label="Close"
              @click="closeForm"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </header>

          <form class="prod-form" @submit.prevent="saveForm">
            <div class="prod-form__grid">
              <div class="prod-form__row prod-form__row--full">
                <label class="prod-form__label">Image</label>
                <div class="prod-image-field">
                  <div
                    class="prod-image-preview"
                    :class="{ 'prod-image-preview--empty': !imagePreviewSrc }"
                  >
                    <img v-if="imagePreviewSrc" :src="imagePreviewSrc" alt="Product preview" />
                    <span v-else class="prod-image-preview__placeholder" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <path d="M21 15l-5-5L5 21"/>
                      </svg>
                    </span>
                  </div>
                  <div class="prod-image-actions">
                    <input
                      ref="imageInput"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      class="prod-image-file-input"
                      @change="onImageSelected"
                    />
                    <div class="prod-image-btn-row">
                      <button type="button" class="prod-image-btn prod-image-btn--primary" @click="triggerImagePicker">
                        {{ imagePreviewSrc ? 'Replace upload' : 'Choose image' }}
                      </button>
                      <button
                        v-if="imagePreviewSrc"
                        type="button"
                        class="prod-image-btn prod-image-btn--ghost"
                        @click="clearImage"
                      >
                        Remove
                      </button>
                    </div>
                    <p class="prod-form__hint prod-image-hint">JPEG, PNG, WEBP or GIF · up to 5 MB.</p>

                    <div class="prod-image-divider"><span>or paste a link</span></div>

                    <input
                      v-model.trim="form.imageUrl"
                      type="url"
                      class="prod-form__input prod-image-url-input"
                      placeholder="https://example.com/product.jpg"
                      maxlength="1024"
                      @input="onImageUrlInput"
                    />
                    <p class="prod-form__hint prod-image-hint">Use a direct image URL when you don't want to upload a copy.</p>
                  </div>
                </div>
              </div>

              <div class="prod-form__row">
                <label for="prod-sku" class="prod-form__label">
                  SKU <span class="prod-form__required">*</span>
                </label>
                <input
                  id="prod-sku"
                  ref="skuInput"
                  v-model.trim="form.sku"
                  type="text"
                  class="prod-form__input"
                  placeholder="e.g. MAP2001"
                  maxlength="255"
                  required
                />
                <p class="prod-form__hint">Unique stock-keeping unit. Must be unique across the system.</p>
              </div>

              <div class="prod-form__row">
                <label for="prod-name" class="prod-form__label">
                  Name <span class="prod-form__required">*</span>
                </label>
                <input
                  id="prod-name"
                  v-model.trim="form.name"
                  type="text"
                  class="prod-form__input"
                  placeholder="e.g. Agro Yield"
                  maxlength="255"
                  required
                />
                <p class="prod-form__hint">Display name shown to staff in orders and reports.</p>
              </div>

              <div class="prod-form__row prod-form__row--full">
                <label for="prod-description" class="prod-form__label">Description</label>
                <textarea
                  id="prod-description"
                  v-model="form.description"
                  class="prod-form__textarea"
                  placeholder="Optional. Notes about the product, variant or pack size."
                  rows="3"
                  maxlength="1000"
                />
                <p class="prod-form__hint">
                  Optional internal note.
                  <span class="prod-form__counter">{{ (form.description || '').length }} / 1000</span>
                </p>
              </div>

              <div class="prod-form__row">
                <label for="prod-barcode" class="prod-form__label">Barcode</label>
                <input
                  id="prod-barcode"
                  v-model.trim="form.barcode"
                  type="text"
                  class="prod-form__input"
                  placeholder="e.g. 0602024647799"
                  maxlength="255"
                />
                <p class="prod-form__hint">EAN/UPC/internal — used for scanning during packing.</p>
              </div>

              <div class="prod-form__row">
                <label for="prod-shortcode" class="prod-form__label">Shortcode</label>
                <input
                  id="prod-shortcode"
                  v-model.trim="form.shortcode"
                  type="text"
                  class="prod-form__input"
                  placeholder="e.g. AY"
                  maxlength="32"
                />
                <p class="prod-form__hint">Short label used in invoices and exports.</p>
              </div>

              <div class="prod-form__row">
                <label for="prod-buying-price" class="prod-form__label">
                  Buying Price <span class="prod-form__required">*</span>
                </label>
                <div class="prod-form__input-affix">
                  <span class="prod-form__affix">₹</span>
                  <input
                    id="prod-buying-price"
                    v-model.number="form.buying_price"
                    type="number"
                    class="prod-form__input prod-form__input--with-prefix"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div class="prod-form__row">
                <label for="prod-selling-price" class="prod-form__label">
                  Selling Price <span class="prod-form__required">*</span>
                </label>
                <div class="prod-form__input-affix">
                  <span class="prod-form__affix">₹</span>
                  <input
                    id="prod-selling-price"
                    v-model.number="form.selling_price"
                    type="number"
                    class="prod-form__input prod-form__input--with-prefix"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              <div class="prod-form__row">
                <label for="prod-weight" class="prod-form__label">
                  Weight <span class="prod-form__required">*</span>
                </label>
                <div class="prod-form__input-affix prod-form__input-affix--suffix">
                  <input
                    id="prod-weight"
                    v-model.number="form.weight"
                    type="number"
                    class="prod-form__input prod-form__input--with-suffix"
                    min="0"
                    step="0.01"
                    required
                  />
                  <span class="prod-form__affix prod-form__affix--suffix">g</span>
                </div>
              </div>

              <div class="prod-form__row">
                <label for="prod-shipping-weight" class="prod-form__label">
                  Shipping Weight <span class="prod-form__required">*</span>
                </label>
                <div class="prod-form__input-affix prod-form__input-affix--suffix">
                  <input
                    id="prod-shipping-weight"
                    v-model.number="form.shipping_weight"
                    type="number"
                    class="prod-form__input prod-form__input--with-suffix"
                    min="0"
                    step="0.01"
                    required
                  />
                  <span class="prod-form__affix prod-form__affix--suffix">g</span>
                </div>
                <p class="prod-form__hint">Includes packaging — used by courier integrations.</p>
              </div>
            </div>

            <CAlert v-if="formError" color="danger" show class="prod-form__alert">{{ formError }}</CAlert>

            <footer class="prod-form__footer">
              <button type="button" class="prod-btn prod-btn--ghost" :disabled="isSaving" @click="closeForm">Cancel</button>
              <button type="submit" class="prod-btn prod-btn--primary" :disabled="!canSubmit || isSaving">
                {{ isSaving ? 'Saving…' : (isEditing ? 'Save changes' : 'Create product') }}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </transition>

    <transition name="prod-fade">
      <div
        v-if="deleteTarget"
        class="prod-modal-backdrop"
        @click.self="closeDelete"
      >
        <div class="prod-modal prod-modal--confirm" role="alertdialog" aria-modal="true" aria-label="Confirm delete">
          <header class="prod-modal__header">
            <div class="prod-modal__title-block">
              <span class="prod-modal__accent prod-modal__accent--danger" aria-hidden="true"></span>
              <div>
                <h2 class="prod-modal__title">Delete product?</h2>
                <p class="prod-modal__subtitle">This cannot be undone.</p>
              </div>
            </div>
            <button
              type="button"
              class="prod-modal__close"
              aria-label="Close"
              @click="closeDelete"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </header>

          <div class="prod-confirm-body">
            <p class="prod-confirm-body__lead">
              "<strong>{{ deleteTarget.name }}</strong>" (<span class="prod-confirm-body__sku">{{ deleteTarget.sku }}</span>) will be removed from the catalogue. Existing orders that reference it will keep their snapshot, but new orders won't be able to add this product.
            </p>
          </div>

          <footer class="prod-form__footer">
            <button type="button" class="prod-btn prod-btn--ghost" :disabled="isDeleting" @click="closeDelete">Cancel</button>
            <button type="button" class="prod-btn prod-btn--danger" :disabled="isDeleting" @click="confirmDelete">
              {{ isDeleting ? 'Deleting…' : 'Delete product' }}
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import BaseTable from '@/components/BaseTable.vue'

const EMPTY_FORM = () => ({
  id: null,
  sku: '',
  name: '',
  description: '',
  barcode: '',
  shortcode: '',
  buying_price: null,
  selling_price: null,
  weight: null,
  shipping_weight: null,
  // Image state: `image` mirrors the currently-saved URL from the
  // server; `imageFile` holds a pending upload; `imageUrl` holds a
  // pending external link (mutually exclusive with imageFile);
  // `removeImage` requests server-side clearing on next save.
  // previewObjectUrl tracks the blob: URL we create for the picked
  // file so we can revoke it and avoid leaking memory across modal
  // open/close cycles.
  image: null,
  imageFile: null,
  imageUrl: '',
  removeImage: false,
  previewObjectUrl: null,
})

const MAX_IMAGE_BYTES = 5 * 1024 * 1024
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

export default {
  name: 'SettingsProducts',
  components: { BaseTable },
  data () {
    return {
      isLoading: false,
      errorMessage: '',
      searchTerm: '',
      items: [],
      columns: [
        { key: 'image', label: '', width: '56px', sortable: false },
        { key: 'sku', label: 'SKU', width: '120px' },
        { key: 'name', label: 'Name' },
        { key: 'shortcode', label: 'Shortcode', width: '110px' },
        { key: 'barcode', label: 'Barcode', width: '160px' },
        { key: 'buying_price', label: 'Buying', width: '110px', align: 'right' },
        { key: 'selling_price', label: 'Selling', width: '110px', align: 'right' },
        { key: 'weight', label: 'Weight', width: '100px', align: 'right' },
        { key: 'shipping_weight', label: 'Ship Wt', width: '100px', align: 'right' },
        { key: 'actions', label: '', width: '170px', align: 'right', sortable: false },
      ],
      formOpen: false,
      form: EMPTY_FORM(),
      formError: '',
      isSaving: false,
      deleteTarget: null,
      isDeleting: false,
    }
  },
  computed: {
    activeOrganisationName () {
      return this.$store?.state?.activeOrganisationName || ''
    },
    filteredItems () {
      const term = this.searchTerm.toLowerCase()
      if (!term) return this.items
      return this.items.filter((item) => {
        return [item.name, item.sku, item.barcode, item.shortcode, item.description]
          .some((field) => (field || '').toString().toLowerCase().includes(term))
      })
    },
    isEditing () {
      return this.form.id !== null && this.form.id !== undefined
    },
    formTitle () {
      return this.isEditing ? 'Edit product' : 'New product'
    },
    formSubtitle () {
      return this.isEditing
        ? 'Update product details — changes apply immediately to new orders.'
        : 'Add a product to the catalogue. Required fields are marked with *.'
    },
    canSubmit () {
      const hasText = this.form.sku.trim().length > 0 && this.form.name.trim().length > 0
      const isNumber = (v) => typeof v === 'number' && !Number.isNaN(v) && v >= 0
      return hasText
        && isNumber(this.form.buying_price)
        && isNumber(this.form.selling_price)
        && isNumber(this.form.weight)
        && isNumber(this.form.shipping_weight)
    },
    imagePreviewSrc () {
      if (this.form.previewObjectUrl) return this.form.previewObjectUrl
      if (this.form.imageUrl) return this.form.imageUrl
      if (this.form.removeImage) return null
      return this.form.image || null
    },
  },
  beforeDestroy () {
    this.revokePreviewUrl()
  },
  created () {
    this.loadData()
  },
  methods: {
    formatMoney (value) {
      if (value === null || value === undefined || value === '') return '—'
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return value
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2,
      }).format(numeric)
    },
    formatWeight (value) {
      if (value === null || value === undefined || value === '') return '—'
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return value
      return `${new Intl.NumberFormat('en-IN').format(numeric)} g`
    },
    loadData () {
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/items`)
        .then((response) => {
          this.items = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load products right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    openCreate () {
      this.revokePreviewUrl()
      this.form = EMPTY_FORM()
      this.formError = ''
      this.formOpen = true
      this.$nextTick(() => {
        if (this.$refs.skuInput) this.$refs.skuInput.focus()
      })
    },
    openEdit (row) {
      this.revokePreviewUrl()
      this.form = {
        ...EMPTY_FORM(),
        id: row.id,
        sku: row.sku || '',
        name: row.name || '',
        description: row.description || '',
        barcode: row.barcode || '',
        shortcode: row.shortcode || '',
        buying_price: row.buying_price !== null && row.buying_price !== undefined ? Number(row.buying_price) : null,
        selling_price: row.selling_price !== null && row.selling_price !== undefined ? Number(row.selling_price) : null,
        weight: row.weight !== null && row.weight !== undefined ? Number(row.weight) : null,
        shipping_weight: row.shipping_weight !== null && row.shipping_weight !== undefined ? Number(row.shipping_weight) : null,
        image: row.image || null,
      }
      this.formError = ''
      this.formOpen = true
      this.$nextTick(() => {
        if (this.$refs.skuInput) this.$refs.skuInput.focus()
      })
    },
    closeForm () {
      if (this.isSaving) return
      this.revokePreviewUrl()
      this.formOpen = false
      this.formError = ''
    },
    triggerImagePicker () {
      if (this.$refs.imageInput) this.$refs.imageInput.click()
    },
    onImageSelected (event) {
      const file = event.target.files && event.target.files[0]
      // Reset the input so picking the same filename again still fires change
      event.target.value = ''
      if (!file) return

      if (!IMAGE_MIME_TYPES.includes(file.type)) {
        this.formError = 'Image must be a JPEG, PNG, WEBP or GIF.'
        return
      }
      if (file.size > MAX_IMAGE_BYTES) {
        this.formError = 'Image is too large. Maximum size is 5 MB.'
        return
      }

      this.revokePreviewUrl()
      this.form.imageFile = file
      this.form.imageUrl = ''
      this.form.removeImage = false
      this.form.previewObjectUrl = URL.createObjectURL(file)
      this.formError = ''
    },
    onImageUrlInput () {
      // Typing a URL clears any pending file upload; the URL becomes
      // the source of truth. Also clears the remove flag — typing
      // a link is an implicit "set the image to this".
      if (!this.form.imageUrl) return
      if (this.form.imageFile || this.form.previewObjectUrl) {
        this.revokePreviewUrl()
        this.form.imageFile = null
      }
      this.form.removeImage = false
      this.formError = ''
    },
    clearImage () {
      this.revokePreviewUrl()
      this.form.imageFile = null
      this.form.imageUrl = ''
      // Only flag server-side removal if there's actually a stored image
      // to remove. For brand-new products this is a no-op.
      this.form.removeImage = !!this.form.image
    },
    revokePreviewUrl () {
      if (this.form && this.form.previewObjectUrl) {
        URL.revokeObjectURL(this.form.previewObjectUrl)
        this.form.previewObjectUrl = null
      }
    },
    async saveForm () {
      if (!this.canSubmit || this.isSaving) return
      this.isSaving = true
      this.formError = ''

      const fields = {
        sku: this.form.sku.trim(),
        name: this.form.name.trim(),
        description: this.form.description ? this.form.description.trim() : '',
        barcode: this.form.barcode ? this.form.barcode.trim() : '',
        shortcode: this.form.shortcode ? this.form.shortcode.trim() : '',
        buying_price: Number(this.form.buying_price),
        selling_price: Number(this.form.selling_price),
        weight: Number(this.form.weight),
        shipping_weight: Number(this.form.shipping_weight),
      }

      // Only file uploads need multipart. URL changes and removals can
      // ride on plain JSON since the controller reads image_url and
      // remove_image off the request regardless of content type.
      const useMultipart = !!this.form.imageFile

      try {
        const url = this.isEditing
          ? `${this.$apiAdress}/api/items/${this.form.id}`
          : `${this.$apiAdress}/api/items`

        if (useMultipart) {
          // Laravel doesn't read multipart bodies on PUT requests — spoof
          // the method via _method when editing so the file upload still
          // routes to ItemController@update.
          const fd = new FormData()
          Object.entries(fields).forEach(([key, value]) => {
            fd.append(key, value === null || value === undefined ? '' : String(value))
          })
          fd.append('image', this.form.imageFile)
          if (this.isEditing) fd.append('_method', 'PUT')
          await axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        } else {
          if (this.form.imageUrl) fields.image_url = this.form.imageUrl
          if (this.form.removeImage) fields.remove_image = true
          if (this.isEditing) {
            await axios.put(url, fields)
          } else {
            await axios.post(url, fields)
          }
        }

        this.$toast.success(this.isEditing ? 'Product updated' : 'Product created')
        this.revokePreviewUrl()
        this.formOpen = false
        await this.refreshSilently()
      } catch (error) {
        const message = error?.response?.data?.error
        if (message && typeof message === 'object') {
          this.formError = Object.values(message).flat().join(' ')
        } else if (typeof message === 'string') {
          this.formError = message
        } else {
          this.formError = 'Save failed. Please try again.'
        }
      } finally {
        this.isSaving = false
      }
    },
    openDelete (row) {
      this.deleteTarget = row
    },
    closeDelete () {
      if (this.isDeleting) return
      this.deleteTarget = null
    },
    async confirmDelete () {
      if (!this.deleteTarget || this.isDeleting) return
      this.isDeleting = true
      try {
        await axios.delete(`${this.$apiAdress}/api/items/${this.deleteTarget.id}`)
        this.$toast.success('Product deleted')
        this.deleteTarget = null
        await this.refreshSilently()
      } catch (error) {
        const message = error?.response?.data?.error || 'Delete failed. Please try again.'
        this.$toast.error(typeof message === 'string' ? message : 'Delete failed.')
      } finally {
        this.isDeleting = false
      }
    },
    async refreshSilently () {
      try {
        const response = await axios.get(`${this.$apiAdress}/api/items`)
        this.items = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        // Ignore — toast on the action already conveyed success.
      }
    },
  },
}
</script>

<style scoped>
.prod-page {
  padding: 0.25rem 0;
}

.prod-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.prod-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1 1 auto;
  min-width: 0;
}

.prod-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.prod-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.prod-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.prod-toolbar__org {
  color: #0e5b46;
  font-weight: 700;
}

.prod-toolbar__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-left: auto;
  flex-shrink: 0;
}

.prod-toolbar__actions >>> .btn.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.prod-toolbar__actions >>> .btn.create-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.prod-toolbar__actions >>> .btn.create-btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.create-btn__icon {
  flex-shrink: 0;
  display: block;
}

.prod-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.prod-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.prod-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.prod-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.prod-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: prod-refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes prod-refresh-spin {
  to { transform: rotate(360deg); }
}

.prod-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.prod-table {
  margin-bottom: 1rem;
}

.prod-search-wrap {
  position: relative;
  width: 320px;
  max-width: 100%;
}

.prod-search-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.prod-input {
  width: 100%;
  height: 34px;
  padding: 0.3rem 2.25rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.prod-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.prod-search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.prod-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

.prod-thumb-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.prod-thumb {
  display: block;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  background: #f3f6fb;
  border: 1px solid #e5e9f2;
}

.prod-thumb--placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  background: #f8fafc;
}

.prod-sku {
  font-family: 'SFMono-Regular', Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.78rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: 0.01em;
}

.prod-name-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.prod-name {
  font-size: 0.86rem;
  font-weight: 600;
  color: #1f2a44;
  word-break: break-word;
}

.prod-description {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 0.72rem;
  color: #6b7280;
  line-height: 1.35;
}

.prod-shortcode-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: #ecfdf5;
  color: #0e5b46;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  border: 1px solid #c7ebda;
  text-transform: uppercase;
}

.prod-barcode {
  font-family: 'SFMono-Regular', Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.74rem;
  color: #4b5563;
}

.prod-num {
  font-variant-numeric: tabular-nums;
  font-size: 0.82rem;
  color: #1f2a44;
}

.prod-num--strong {
  font-weight: 700;
  color: #0e5b46;
}

.prod-muted {
  font-size: 0.78rem;
  color: #9ca3af;
}

.prod-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: flex-end;
}

.prod-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.prod-action-btn--edit:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}

.prod-action-btn--delete:hover {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.12);
}

.prod-empty {
  text-align: center;
  padding: 1.5rem 1rem;
}

.prod-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.prod-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.prod-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.prod-empty__link:hover {
  text-decoration: underline;
}

.prod-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}

.prod-modal {
  width: 720px;
  max-width: 100%;
  max-height: calc(100vh - 2.5rem);
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.prod-modal--confirm {
  width: 480px;
}

.prod-modal__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.1rem 0.9rem;
  border-bottom: 1px solid #e5e9f2;
  background: #fbfcfe;
}

.prod-modal__header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.prod-modal--confirm .prod-modal__header::before {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
}

.prod-modal__title-block {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  min-width: 0;
}

.prod-modal__accent {
  margin-top: 4px;
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  flex: 0 0 auto;
}

.prod-modal__accent--danger {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
}

.prod-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.prod-modal__subtitle {
  margin: 2px 0 0;
  font-size: 0.74rem;
  color: #6b7280;
  line-height: 1.3;
}

.prod-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.prod-modal__close:hover {
  background: #f3f6fb;
  border-color: #c7cfdb;
  color: #1f2a44;
}

.prod-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1.1rem 1.1rem 0;
  overflow-y: auto;
}

.prod-form__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem 1rem;
}

.prod-form__row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.prod-form__row--full {
  grid-column: 1 / -1;
}

.prod-form__label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.prod-form__required {
  color: #ef4444;
  margin-left: 2px;
}

.prod-form__input,
.prod-form__textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.86rem;
  color: #1f2a44;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.prod-form__input {
  height: 38px;
}

.prod-form__textarea {
  resize: vertical;
  min-height: 80px;
  line-height: 1.45;
}

.prod-form__input:focus,
.prod-form__textarea:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.prod-form__input-affix {
  position: relative;
}

.prod-form__affix {
  position: absolute;
  top: 50%;
  left: 0.7rem;
  transform: translateY(-50%);
  font-size: 0.84rem;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
}

.prod-form__affix--suffix {
  left: auto;
  right: 0.7rem;
}

.prod-form__input--with-prefix {
  padding-left: 1.7rem;
}

.prod-form__input--with-suffix {
  padding-right: 1.7rem;
}

.prod-form__hint {
  margin: 0;
  font-size: 0.72rem;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.prod-form__counter {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #4b5563;
}

.prod-image-field {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}

.prod-image-preview {
  width: 96px;
  height: 96px;
  flex: 0 0 auto;
  border-radius: 10px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.prod-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.prod-image-preview--empty {
  background: repeating-linear-gradient(
    45deg,
    #f8fafc,
    #f8fafc 6px,
    #f1f5f9 6px,
    #f1f5f9 12px
  );
  border-style: dashed;
  color: #94a3b8;
}

.prod-image-preview__placeholder {
  display: inline-flex;
}

.prod-image-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.45rem;
  min-width: 0;
  flex: 1 1 auto;
}

.prod-image-file-input {
  display: none;
}

.prod-image-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.prod-image-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.prod-image-btn--primary:hover {
  filter: brightness(1.05);
  box-shadow: 0 4px 10px rgba(14, 91, 70, 0.24);
}

.prod-image-btn--ghost {
  background: #ffffff;
  border-color: #e5e9f2;
  color: #b91c1c;
}

.prod-image-btn--ghost:hover {
  border-color: #ef4444;
  background: #fef2f2;
}

.prod-image-hint {
  margin: 0.1rem 0 0;
}

.prod-image-btn-row {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.prod-image-divider {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.45rem;
  width: 100%;
  color: #94a3b8;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.prod-image-divider::before,
.prod-image-divider::after {
  content: '';
  flex: 1 1 auto;
  height: 1px;
  background: #e5e9f2;
}

.prod-image-url-input {
  width: 100%;
}

.prod-form__alert {
  margin: 0;
  border-radius: 8px;
  font-size: 0.78rem;
}

.prod-form__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.9rem 1.1rem 1.1rem;
  margin-top: 0.4rem;
  border-top: 1px solid #eef1f7;
  background: #fbfcfe;
}

.prod-confirm-body {
  padding: 1rem 1.1rem 0;
}

.prod-confirm-body__lead {
  margin: 0;
  font-size: 0.86rem;
  color: #1f2a44;
  line-height: 1.5;
}

.prod-confirm-body__lead strong {
  color: #0e5b46;
}

.prod-confirm-body__sku {
  font-family: 'SFMono-Regular', Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.8rem;
  color: #4b5563;
}

.prod-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease, transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease;
  border: 1px solid transparent;
}

.prod-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.prod-btn--ghost {
  background: #ffffff;
  border-color: #d8dde6;
  color: #1f2a44;
}

.prod-btn--ghost:hover:not(:disabled) {
  background: #f3f6fb;
  border-color: #c7cfdb;
}

.prod-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.prod-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.prod-btn--danger {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(185, 28, 28, 0.2);
}

.prod-btn--danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(185, 28, 28, 0.28);
  filter: brightness(1.05);
}

.prod-fade-enter-active,
.prod-fade-leave-active {
  transition: opacity 0.18s ease;
}

.prod-fade-enter,
.prod-fade-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .prod-toolbar {
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .prod-toolbar__title {
    flex: 1 1 100%;
  }
  .prod-toolbar__actions {
    flex: 1 1 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
  .prod-search-wrap {
    width: 100%;
  }
  .prod-modal {
    width: 100%;
  }
  .prod-form__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575px) {
  .prod-page {
    padding: 0 0 0.5rem;
  }
  .prod-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .prod-toolbar__accent {
    height: 30px;
  }
  .prod-toolbar__title h1 {
    font-size: 1rem;
  }
  .prod-toolbar__title p {
    font-size: 0.72rem;
  }
  .prod-toolbar__actions {
    gap: 0.4rem;
  }
  .prod-toolbar__actions >>> .btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .prod-modal-backdrop {
    padding: 0.5rem;
    align-items: flex-end;
  }
  .prod-modal {
    max-height: calc(100vh - 1rem);
    border-radius: 12px;
  }
  .prod-modal__header {
    padding: 0.85rem 0.85rem 0.75rem;
  }
  .prod-modal__title {
    font-size: 0.94rem;
  }
  .prod-modal__subtitle {
    font-size: 0.7rem;
  }
  .prod-row-actions {
    flex-wrap: wrap;
    gap: 0.3rem;
  }
}
</style>
